import { useState } from 'react';
import { usePagedList } from '@/hooks/usePagedList';
import { ListFooter } from '@/features/common/ListFooter';
import { Bot, History, Search } from 'lucide-react';
import { AUDIT_CATEGORIES, labelForAction, type AuditCategory } from '@fint/validation';
import { qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useDebounced } from '@/hooks/useDebounced';
import { useIsPhone } from '@/hooks/useIsPhone';
import { PageHeader } from '@/app/DashboardLayout';
import { formatDateTime } from '@/lib/dates';
import { describeMetadata } from './changes';
import { cn } from '@/lib/cn';
import {
  Badge, Card, CardLine, CardRow, Cards, EmptyState, ErrorState, Input, SkeletonTable,
  TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';

interface AuditEvent {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
}

/**
 * The `via` an MCP request leaves on everything it causes. See
 * `common/actor-context.ts` — `actorId` stays the person who authorised the
 * key, and this records that a model acted on their behalf.
 */
interface ActorVia {
  kind?: string;
  keyName?: string;
}

/**
 * Everything except what Claude read.
 *
 * A model working for an hour leaves far more rows than a person does, and an
 * unfiltered feed would bury the figure that changed under fifty reads. The
 * chip is one click away, and the record is complete either way — which is what
 * made it safe to start recording reads at all.
 */
const DEFAULT_CATEGORIES = AUDIT_CATEGORIES.map((c) => c.key).filter((key) => key !== 'assistant');

/**
 * Who did what, and when.
 *
 * `audit_events` has been written on every mutation since the first release and
 * was never readable — a write-only audit trail is a cost with no benefit. This
 * is the page an auditor asks for and the page you open when a figure changed
 * and nobody remembers changing it.
 *
 * ── THE PHRASE AND THE SLUG, BOTH ──
 * Rows used to render the raw action: `bank_transaction.categorised`. The
 * argument for that was real — a CA quoting one in an email wants the string
 * the system actually recorded — but it made a page nobody could skim, and it
 * only held because there was no phrasing to show instead. There is now, shared
 * with the server's category filter so the two cannot drift
 * (`audit-actions.ts`). So the row reads as a sentence and still carries the
 * slug beside it.
 *
 * ── THE FILTERS RUN ON THE SERVER ──
 * They used to run in the browser over the hundred rows already loaded, so
 * searching a busy month found whatever happened to be on page one and reported
 * the rest as "nothing matches".
 */
export function AuditPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const isPhone = useIsPhone();
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [search, setSearch] = useState('');
  // Typing is a keystroke a request otherwise, and this one filters a log.
  const q = useDebounced(search.trim(), 300);

  const query = usePagedList<AuditEvent, { nextBefore?: string }>({
    key: ['audit', org?.id, categories.join(','), q],
    path: (before) => `/audit${qs({ categories: categories.join(','), q, before, limit: 100 })}`,
    // The log pages on a timestamp and names it `nextBefore` - see usePagedList.
    cursorOf: (page) => page.nextBefore,
    enabled: !!org?.id,
  });

  const toggle = (key: AuditCategory) =>
    setCategories((current) =>
      current.includes(key) ? current.filter((c) => c !== key) : [...current, key],
    );

  const filtered = q.length > 0 || categories.length !== AUDIT_CATEGORIES.length;

  return (
    <>
      <PageHeader title="Activity" subtitle="Every change, with the person who made it." />

      <div className="space-y-4 px-5 pb-8">
        <div className="relative max-w-sm">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            className="pl-8"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by action, person or id"
            aria-label="Search activity"
          />
        </div>

        {/* Chips rather than a select, because these combine: "money in and out,
            and what Claude did" is one question, and a dropdown makes it two. */}
        <div className="flex flex-wrap gap-1.5">
          {AUDIT_CATEGORIES.map((category) => {
            const on = categories.includes(category.key);
            return (
              <button
                key={category.key}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(category.key)}
                className={cn(
                  'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors touch:min-h-11',
                  on
                    // Full-strength borders on both states: the chip's edge is
                    // its boundary, and at /40 the selected one measured
                    // 1.81:1 against the page, the unselected 1.23:1.
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-input bg-background text-muted-foreground hover:bg-muted',
                )}
              >
                {category.key === 'assistant' ? <Bot className="size-3.5" aria-hidden /> : null}
                {category.label}
              </button>
            );
          })}
        </div>

        {/* Unticking the last chip does not mean "show nothing" - it means no
            filter, which is the server's rule and the conventional one for
            chips. Said out loud because the step is otherwise surprising: it
            shows MORE, and the extra rows are the Claude ones the default
            leaves out. */}
        {categories.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No filter, so this is everything &mdash; including what Claude read.
          </p>
        ) : null}

        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : query.isLoading ? (
            <SkeletonTable rows={8} cols={4} />
          ) : query.items.length ? (
            <>
              {isPhone ? (
                <Cards>
                  {query.items.map((event) => (
                    <CardRow key={event.id}>
                      <CardLine
                        title={
                          <span className="flex items-center gap-1.5">
                            {viaOf(event) ? <Bot className="size-3.5 shrink-0 text-muted-foreground" aria-hidden /> : null}
                            <span className="truncate">{event.actorName ?? 'System'}</span>
                          </span>
                        }
                        meta={labelForAction(event.action)}
                      />
                      <div className="mt-1 text-xs text-muted-foreground">
                        {formatDateTime(event.createdAt)}
                        {event.metadata ? ` · ${describe(event.metadata)}` : ''}
                      </div>
                    </CardRow>
                  ))}
                </Cards>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>When</TH>
                      <TH>Who</TH>
                      <TH>What</TH>
                      <TH>Record</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {query.items.map((event) => {
                      const via = viaOf(event);
                      return (
                        <TR key={event.id}>
                          <TD className="whitespace-nowrap text-muted-foreground">
                            {formatDateTime(event.createdAt)}
                          </TD>
                          <TD>
                            <div className="font-medium">{event.actorName ?? 'System'}</div>
                            {/* Whose account it was is not the whole story once a
                                key exists. "Priya did this" and "Claude did this
                                on Priya's behalf, using the key called Laptop"
                                are different facts and this is the page where
                                the difference matters. */}
                            {via ? (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Bot className="size-3" aria-hidden />
                                via AI agent{via.keyName ? ` · ${via.keyName}` : ''}
                              </div>
                            ) : event.actorEmail ? (
                              <div className="text-xs text-muted-foreground">{event.actorEmail}</div>
                            ) : null}
                          </TD>
                          <TD>
                            <div className="font-medium">{labelForAction(event.action)}</div>
                            {/* The recorded string, kept: it is what somebody
                                quotes when they ask why a figure moved. */}
                            <div className="font-mono text-xs text-muted-foreground">{event.action}</div>
                          </TD>
                          <TD className="text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Badge
                                variant={
                                  event.action.includes('deleted') || event.action.includes('voided')
                                    ? 'danger'
                                    : 'neutral'
                                }
                              >
                                {event.entityType}
                              </Badge>
                            </div>
                            {event.metadata ? (
                              <div className="mt-0.5 max-w-[420px] truncate text-xs">{describe(event.metadata)}</div>
                            ) : null}
                          </TD>
                        </TR>
                      );
                    })}
                  </TBody>
                </Table>
              )}
              <ListFooter query={query} noun="events" />
            </>
          ) : (
            <EmptyState
              icon={History}
              title={filtered ? 'Nothing matches that filter' : 'Nothing recorded yet'}
              description={
                filtered
                  ? undefined
                  : 'Every invoice, payment and setting change is logged here as it happens.'
              }
            />
          )}
        </Card>

        {!perms.seesAll('audit') ? (
          <p className="text-xs text-muted-foreground">
            You are seeing only your own activity. An administrator sees everyone&rsquo;s.
          </p>
        ) : null}
      </div>
    </>
  );
}

/** The `via` block, when a model caused this row. */
function viaOf(event: AuditEvent): ActorVia | undefined {
  const via = event.metadata?.via;
  return via && typeof via === 'object' ? (via as ActorVia) : undefined;
}

/**
 * The metadata blob as one readable line, rather than raw JSON.
 *
 * `via` is dropped: it is rendered beside the actor, where it belongs, and
 * printing it here as well would put "via: [object Object]" on every row a key
 * touched.
 */
function describe(metadata: Record<string, unknown>): string {
  return describeMetadata(metadata);
}
